import React, { useState, useRef, useEffect, useCallback } from 'react';
import { VideoChunkLoader, getOptimalChunkSize, detectServerCapabilities } from '../utils/videoStreaming';
import VideoLoadingBackground from './VideoLoadingBackground';

const StreamingVideo = ({
  src,
  className = '',
  style = {},
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  showProgress = true,
  onLoadProgress = null,
  useChunkedLoading = 'auto' // 'auto', true, false
}) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [chunkProgress, setChunkProgress] = useState({ loaded: 0, total: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const [canPlay, setCanPlay] = useState(false);
  const [error, setError] = useState(null);
  const [videoSrc, setVideoSrc] = useState('');
  const [loadingMethod, setLoadingMethod] = useState('Detecting server capabilities...');
  const [serverCapabilities, setServerCapabilities] = useState(null);
  const [detectionComplete, setDetectionComplete] = useState(false);
  const videoRef = useRef(null);
  const chunkLoaderRef = useRef(null);

  const handleLoadStart = useCallback(() => {
    setIsLoaded(false);
    setCanPlay(false);
    setError(null);
    setLoadingProgress(0);
  }, []);

  const handleProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.buffered.length) return;

    // Calculate buffered percentage
    const buffered = video.buffered;
    const duration = video.duration;

    if (duration > 0) {
      let bufferedAmount = 0;
      for (let i = 0; i < buffered.length; i++) {
        bufferedAmount += buffered.end(i) - buffered.start(i);
      }
      const percentage = Math.min((bufferedAmount / duration) * 100, 100);
      setLoadingProgress(percentage);

      if (onLoadProgress) {
        onLoadProgress(percentage);
      }

      // If we have enough buffer (10 seconds or 20% of video), allow playback
      const hasEnoughBuffer = bufferedAmount >= Math.min(10, duration * 0.2);
      if (hasEnoughBuffer && !canPlay) {
        setCanPlay(true);
      }
    }
  }, [onLoadProgress, canPlay]);

  const handleCanPlay = useCallback(() => {
    setCanPlay(true);
  }, []);

  const handleCanPlayThrough = useCallback(() => {
    setIsLoaded(true);
    setLoadingProgress(100);
    if (onLoadProgress) {
      onLoadProgress(100);
    }
  }, [onLoadProgress]);

  const handleError = useCallback((e) => {
    const errorMsg = e.target.error ?
      `Video error: ${e.target.error.message} (Code: ${e.target.error.code})` :
      'Failed to load video';
    setError(errorMsg);
    console.error('Video loading error:', errorMsg);
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    // Start buffering immediately after metadata loads
    const video = videoRef.current;
    if (video) {
      video.preload = 'auto';
    }
  }, []);

  // Server detection and video loading effect
  useEffect(() => {
    if (!src) return;

    const detectAndLoad = async () => {
      try {
        // Step 1: Detect server capabilities
        setLoadingMethod('Detecting server capabilities...');
        setLoadingProgress(5);

        const capabilities = await detectServerCapabilities(src);
        setServerCapabilities(capabilities);
        setDetectionComplete(true);

        // Step 2: Determine loading method
        const shouldUseChunks = useChunkedLoading === 'auto' ?
          capabilities.canStream : useChunkedLoading;

        if (shouldUseChunks && capabilities.canStream) {
          // Use chunked streaming
          setLoadingMethod(`Chunked Streaming (${Math.round(capabilities.totalSize / (1024*1024))}MB)`);
          setLoadingProgress(10);

          const chunkSize = getOptimalChunkSize();
          chunkLoaderRef.current = new VideoChunkLoader(src, chunkSize);

          const result = await chunkLoaderRef.current.loadInitialChunks(3, (progress, loadedChunks, totalChunks) => {
            const adjustedProgress = 10 + (progress * 0.9); // 10-100%
            setLoadingProgress(adjustedProgress);
            setChunkProgress({ loaded: loadedChunks, total: totalChunks });

            if (onLoadProgress) {
              onLoadProgress(adjustedProgress);
            }

            // Can play when first chunk is loaded
            if (progress >= 33 && !canPlay) {
              setCanPlay(true);
            }
          });

          // Create blob URL from chunks
          const blobUrl = chunkLoaderRef.current.createBlobURL(result.chunks);
          if (blobUrl) {
            setVideoSrc(blobUrl);
            setIsLoaded(true);
            setLoadingProgress(100);
          } else {
            throw new Error('Failed to create video blob');
          }
        } else {
          // Use progressive download
          const reason = !capabilities.canStream ?
            (capabilities.supportsRanges ? 'File too small' : 'No range support') :
            'User preference';

          setLoadingMethod(`Progressive Download (${reason})`);
          setVideoSrc(src);
          setCanPlay(true);
          setLoadingProgress(15);

          // Simulate progress for better UX
          let progress = 15;
          const progressInterval = setInterval(() => {
            progress += Math.random() * 15;
            if (progress >= 100) {
              progress = 100;
              clearInterval(progressInterval);
              setIsLoaded(true);
            }
            setLoadingProgress(progress);
            if (onLoadProgress) {
              onLoadProgress(progress);
            }
          }, 500);
        }
      } catch (error) {
        console.error('Video loading failed:', error);
        setLoadingMethod('Progressive Download (Fallback)');
        setVideoSrc(src);
        setCanPlay(true);
        setError(null);
        setLoadingProgress(100);
        setIsLoaded(true);
      }
    };

    detectAndLoad();

    return () => {
      if (chunkLoaderRef.current) {
        chunkLoaderRef.current.cleanup();
      }
    };
  }, [src, useChunkedLoading, onLoadProgress, canPlay]);

  // Regular video events (for fallback and non-chunked)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    // Skip if chunked loading is successful
    if (useChunkedLoading && loadingMethod === 'Chunked Streaming' && !error) return;

    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('error', handleError);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    const progressInterval = setInterval(() => {
      if (video && !isLoaded) {
        handleProgress();
      }
    }, 500);

    return () => {
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      clearInterval(progressInterval);
    };
  }, [videoSrc, useChunkedLoading, loadingMethod, error, handleLoadStart, handleProgress, handleCanPlay, handleCanPlayThrough, handleError, handleLoadedMetadata, isLoaded]);

  return (
    <div className={`relative ${className}`} style={style}>
      {/* D3.js Animated Loading Background */}
      {showProgress && !isLoaded && (
        <VideoLoadingBackground
          progress={loadingProgress}
          isLoading={!isLoaded}
        />
      )}

      {/* Loading Progress Indicator */}
      {showProgress && !isLoaded && !error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-30">
          <div className="bg-black bg-opacity-40 backdrop-blur-sm rounded-lg p-6 text-center border border-primary-400/20">
            <div className="w-16 h-16 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white text-sm mb-1">Loading background video...</p>
            <p className="text-primary-400 text-xs mb-2">{loadingMethod}</p>

            {/* Server capabilities info */}
            {detectionComplete && serverCapabilities && (
              <div className="text-xs text-gray-300 mb-2">
                Size: {Math.round(serverCapabilities.totalSize / (1024*1024))}MB •
                Range Support: {serverCapabilities.supportsRanges ? '✓' : '✗'} •
                Streaming: {serverCapabilities.canStream ? '✓' : '✗'}
              </div>
            )}

            <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-r from-primary-400 to-primary-500 transition-all duration-300 ease-out"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
            <p className="text-gray-300 text-xs">
              {loadingProgress.toFixed(1)}% loaded
              {chunkProgress.total > 0 &&
                ` • Chunk ${chunkProgress.loaded}/${chunkProgress.total}`
              }
              {canPlay && !isLoaded && " • Ready to play"}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-20">
          <div className="bg-red-900 bg-opacity-20 backdrop-blur-sm rounded-lg p-6 text-center">
            <div className="w-12 h-12 text-red-400 mx-auto mb-3">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-red-400 text-sm mb-1">Video loading failed</p>
            <p className="text-gray-400 text-xs">{error}</p>
          </div>
        </div>
      )}

      {/* Video Element */}
      <video
        ref={videoRef}
        className={`w-full h-full object-cover pointer-events-none ${canPlay ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          ...style
        }}
        autoPlay={autoPlay && canPlay}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        preload="metadata" // Start with metadata, then auto-load
        crossOrigin="anonymous"
      >
        {videoSrc && <source src={videoSrc} type="video/mp4" />}
        {/* Fallback for browsers that don't support video */}
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
          <p className="text-gray-400">Your browser doesn't support video playback.</p>
        </div>
      </video>
    </div>
  );
};

export default StreamingVideo;